import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useResetForgotPassword } from "./api";
import pwVisibleIcon from "../../assets/pw_visible.svg";
import {
  resetPasswordSchema,
  ResetPasswordFormData,
} from "./schemas/resetPasswordSchema";
import zxcvbn from "zxcvbn";

export default function ResetForgotPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const resetMutation = useResetForgotPassword();

  const onSubmit = (data: ResetPasswordFormData) => {
    if (!navigator.onLine) {
      toast.error("No internet connection. Please check your network.");
      return;
    }
    if (!token) {
      toast.error("Reset link is missing or invalid");
      return;
    }
    const result = zxcvbn(data.password, []);
    if (result.score < 3) {
      toast.error(
        result.feedback.warning || "Password is too weak or too common"
      );
      return;
    }
    resetMutation.mutate(
      { ...data, token },
      {
        onSuccess: () => {
          toast.success("Password reset successfully", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            theme: "colored",
          });
          navigate("/log-in?reset=1");
        },
        onError: (err: any) => {
          toast.error(
            err.response?.data?.message ||
              "An error occurred while resetting your password"
          );
        },
      }
    );
  };

  return (
    <div className='flex justify-center'>
      <div className='flex flex-col border border-[#D9D9D9] w-3/4 rounded-md items-center p-6 space-y-4 sm:w-3/4 md:w-1/2 lg:w-1/3'>
        <h1 className='text-lg font-bold'>Enter Your New Password</h1>

        <form onSubmit={handleSubmit(onSubmit)} className='w-full space-y-4'>
          <div className='mb-5'>
            <div className='flex flex-col w-full relative mb-6'>
              <label htmlFor='password' className='mb-1'>
                Password
              </label>
              <input
                id='password'
                type={isPasswordVisible ? "text" : "password"}
                autoComplete='new-password'
                {...register("password")}
                className='border border-[#D9D9D9] rounded-md px-2 py-1 outline-none w-full'
                placeholder='Password'
              />
              <button
                type='button'
                tabIndex={0}
                aria-label={
                  isPasswordVisible ? "Hide password" : "Show password"
                }
                className='absolute right-2 top-8'
                onClick={() => setIsPasswordVisible((v) => !v)}
              >
                <img
                  src={pwVisibleIcon}
                  alt='toggle visibility'
                  className='w-7 h-7'
                />
              </button>
              {errors.password && (
                <p className='text-red-500 text-sm -mb-5'>
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className='flex flex-col w-full relative mb-6'>
              <label htmlFor='confirm-password' className='mb-1'>
                Confirm Password
              </label>
              <input
                id='confirm-password'
                type={isConfirmPasswordVisible ? "text" : "password"}
                autoComplete='new-password'
                {...register("confirmPassword")}
                className='border border-[#D9D9D9] rounded-md px-2 py-1 outline-none w-full'
                placeholder='Confirm Password'
              />
              <button
                type='button'
                tabIndex={0}
                aria-label={
                  isConfirmPasswordVisible ? "Hide password" : "Show password"
                }
                className='absolute right-2 top-8'
                onClick={() => setIsConfirmPasswordVisible((v) => !v)}
              >
                <img
                  src={pwVisibleIcon}
                  alt='toggle visibility'
                  className='w-7 h-7'
                />
              </button>
              {errors.confirmPassword && (
                <p className='text-red-500 text-sm -mb-5'>
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>

          <button
            type='submit'
            disabled={resetMutation.isPending}
            className='bg-[#0275D8] text-white rounded-md w-full py-2 mt-2 hover:bg-[#0256A1] disabled:bg-gray-400'
          >
            {resetMutation.isPending ? "Saving…" : "Save"}
          </button>
        </form>

        <Link to='/log-in' className='underline underline-offset-2 mt-4'>
          Back to login
        </Link>
      </div>
    </div>
  );
}
